import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, usePermission } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import QuotesPage from "@/pages/QuotesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import ContactsPage from "@/pages/ContactsPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicTermsPage from "@/pages/PublicTermsPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentResultPages";
import EmbedLeadFormPage from "@/pages/EmbedLeadFormPage";
import CalendarPage from "@/pages/CalendarPage";
import InventoryPage from "@/pages/InventoryPage";
import SettingsPage from "@/pages/SettingsPage";
import SchedulePage from "@/pages/SchedulePage";
import ArchivePage from "@/pages/ArchivePage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import BillingPage from "@/pages/BillingPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import { BillingProvider } from "@/context/BillingContext";
import { TenantProvider } from "@/context/TenantContext";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

/** Admin OR Office — front-desk staff share this scope. Custom roles
 *  inheriting from office (base_role='office') also pass so the admin
 *  doesn't have to touch code to add a "Manager" role. */
function AdminOrOffice({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const effective = user.base_role || user.role;
  if (user.role !== "admin" && effective !== "office" && effective !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

/** RBAC-based route guard — admins bypass, everyone else needs the given
 *  permission key. Used to gate the sidebar-linkable pages so custom roles
 *  and toggled permissions actually take effect at the URL level. */
function RequirePerm({ perm, children }) {
  const { user } = useAuth();
  const has = usePermission(perm);
  if (!user) return <Navigate to="/login" replace />;
  if (!has) return <Navigate to="/" replace />;
  return children;
}

function PlatformShell() {
  return (
    <Layout>
      <Routes>
        <Route path="/platform/:section" element={<PlatformAdminPage />} />
        <Route path="*" element={<Navigate to="/platform/overview" replace />} />
      </Routes>
    </Layout>
  );
}

function AppShell() {
  const { user } = useAuth();
  // Master account is locked into the 4-tab platform console.
  if (user?.is_platform_admin && !user?.is_impersonating) return <PlatformShell />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/leads" element={<RequirePerm perm="view_leads"><LeadsPage /></RequirePerm>} />
        <Route path="/quotes" element={<RequirePerm perm="view_quotes"><QuotesPage /></RequirePerm>} />
        <Route path="/invoices" element={<RequirePerm perm="view_invoices"><InvoicesPage /></RequirePerm>} />
        <Route path="/contacts" element={<AdminOrOffice><ContactsPage /></AdminOrOffice>} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:id" element={<JobDetailPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/inventory" element={<RequirePerm perm="manage_inventory"><InventoryPage /></RequirePerm>} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/analytics" element={<RequirePerm perm="view_analytics"><AnalyticsPage /></RequirePerm>} />
        <Route path="/settings" element={<AdminOrOffice><SettingsPage /></AdminOrOffice>} />
        <Route path="/billing" element={<AdminOrOffice><BillingPage /></AdminOrOffice>} />
        <Route path="/archive" element={<AdminOrOffice><ArchivePage /></AdminOrOffice>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BillingProvider>
            <TenantProvider>
              <BrowserRouter>
                <ThemedToaster />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/public/quote/:token" element={<PublicQuotePage />} />
                  <Route path="/public/booking/:token" element={<PublicBookingPage />} />
              <Route path="/public/terms/:token" element={<PublicTermsPage />} />
                  <Route path="/embed/lead-form" element={<EmbedLeadFormPage />} />
                  <Route path="/invite/:token" element={<InviteAcceptPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />
                  <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                  <Route path="/*" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
                </Routes>
              </BrowserRouter>
            </TenantProvider>
          </BillingProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return (
    <Toaster
      theme={isLight ? "light" : "dark"}
      position="bottom-right"
      duration={2000}
      toastOptions={{
        style: isLight
          ? { background: "rgba(255,255,255,0.96)", border: "1px solid rgba(15,23,42,0.1)", color: "#0F172A", backdropFilter: "blur(16px)" }
          : { background: "rgba(20,20,20,0.9)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", backdropFilter: "blur(16px)" },
      }}
    />
  );
}

export default App;
